import React, { useState, useRef, useEffect } from "react";

// ─── ANALYTICS DASHBOARD ──────────────────────────────────────
// Drawdown curve, P&L heatmap (day×hour), streak analysis, session breakdown

const fmt = {
  usd: v => (v >= 0 ? "+" : "-") + "$" + Math.abs(v).toFixed(0),
  pct: v => (v >= 0 ? "+" : "") + v.toFixed(1) + "%",
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 14 }, (_, i) => `${String(i + 8).padStart(2, "0")}:00`);

function buildEquityCurve(history, startBalance = 50000) {
  const sorted = [...history].filter(t => t.closedAt && t.pnl != null)
    .sort((a, b) => a.closedAt - b.closedAt);
  let balance = startBalance;
  let peak = startBalance;
  let maxDD = 0;
  let currentDD = 0;
  const curve = [{ ts: Date.now() - 30 * 86400000, balance, dd: 0 }];
  sorted.forEach(t => {
    balance += t.pnl;
    if (balance > peak) { peak = balance; currentDD = 0; }
    else { currentDD = (peak - balance) / peak * 100; }
    maxDD = Math.max(maxDD, currentDD);
    curve.push({ ts: t.closedAt, balance, dd: currentDD, pnl: t.pnl });
  });
  return { curve, maxDD, finalBalance: balance, peak };
}

function buildHeatmap(history) {
  // [dayOfWeek][hour] = { pnl, count }
  const grid = Array.from({ length: 7 }, () =>
    Array.from({ length: 14 }, () => ({ pnl: 0, count: 0 }))
  );
  history.forEach(t => {
    if (!t.closedAt || t.pnl == null) return;
    const d = new Date(t.closedAt);
    const day = d.getDay();
    const hour = d.getHours() - 8;
    if (hour >= 0 && hour < 14) {
      grid[day][hour].pnl += t.pnl;
      grid[day][hour].count += 1;
    }
  });
  return grid;
}

function buildSessionStats(history) {
  const sessions = { "Pre-Market": { pnl: 0, count: 0, wins: 0 }, "RTH": { pnl: 0, count: 0, wins: 0 }, "After-Hours": { pnl: 0, count: 0, wins: 0 } };
  history.forEach(t => {
    if (!t.closedAt || t.pnl == null) return;
    const h = new Date(t.closedAt).getHours();
    const sess = h < 9 ? "Pre-Market" : h < 16 ? "RTH" : "After-Hours";
    sessions[sess].pnl += t.pnl;
    sessions[sess].count += 1;
    if (t.pnl > 0) sessions[sess].wins += 1;
  });
  return sessions;
}

function buildDayStats(history) {
  const dayMap = {};
  history.forEach(t => {
    if (!t.closedAt || t.pnl == null) return;
    const key = new Date(t.closedAt).toDateString();
    if (!dayMap[key]) dayMap[key] = { pnl: 0, count: 0 };
    dayMap[key].pnl += t.pnl;
    dayMap[key].count += 1;
  });
  return Object.entries(dayMap)
    .map(([date, d]) => ({ date, ...d }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

// ─── Equity + Drawdown Canvas ──────────────────────────────────
function EquityCurveChart({ curve }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || curve.length < 2) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#0d0f14";
    ctx.fillRect(0, 0, W, H);

    const pad = { l: 56, r: 16, t: 16, b: 40 };
    const cW = W - pad.l - pad.r;
    const cH = H - pad.t - pad.b;

    // Split into equity (top 60%) and drawdown (bottom 40%)
    const eH = cH * 0.6;
    const dH = cH * 0.38;
    const dY = pad.t + eH + cH * 0.02;

    const balances = curve.map(p => p.balance);
    const maxB = Math.max(...balances);
    const minB = Math.min(...balances);
    const rangeB = maxB - minB || 1;

    const dds = curve.map(p => p.dd);
    const maxDD = Math.max(...dds, 1);

    const px = i => pad.l + (i / (curve.length - 1)) * cW;
    const pyEq = b => pad.t + eH - ((b - minB) / rangeB) * eH;
    const pyDD = d => dY + (d / maxDD) * dH;

    // Grid equity
    ctx.strokeStyle = "#1a1d27";
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = pad.t + (eH / 4) * i;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke();
      const val = maxB - (rangeB / 4) * i;
      ctx.fillStyle = "#3a3d4a";
      ctx.font = "10px monospace";
      ctx.textAlign = "right";
      ctx.fillText("$" + Math.round(val / 1000) + "k", pad.l - 4, y + 3);
    }

    // Zero line for DD
    ctx.strokeStyle = "#1a1d27";
    ctx.beginPath(); ctx.moveTo(pad.l, dY); ctx.lineTo(W - pad.r, dY); ctx.stroke();

    // Equity area fill
    const eGrad = ctx.createLinearGradient(0, pad.t, 0, pad.t + eH);
    eGrad.addColorStop(0, "rgba(0,200,150,0.25)");
    eGrad.addColorStop(1, "rgba(0,200,150,0)");
    ctx.fillStyle = eGrad;
    ctx.beginPath();
    ctx.moveTo(px(0), pad.t + eH);
    curve.forEach((p, i) => ctx.lineTo(px(i), pyEq(p.balance)));
    ctx.lineTo(px(curve.length - 1), pad.t + eH);
    ctx.closePath();
    ctx.fill();

    // Equity line
    ctx.strokeStyle = "#00c896";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    curve.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), pyEq(p.balance)) : ctx.lineTo(px(i), pyEq(p.balance)));
    ctx.stroke();

    // Drawdown area
    const ddGrad = ctx.createLinearGradient(0, dY, 0, dY + dH);
    ddGrad.addColorStop(0, "rgba(240,59,59,0.05)");
    ddGrad.addColorStop(1, "rgba(240,59,59,0.25)");
    ctx.fillStyle = ddGrad;
    ctx.beginPath();
    ctx.moveTo(px(0), dY);
    curve.forEach((p, i) => ctx.lineTo(px(i), pyDD(p.dd)));
    ctx.lineTo(px(curve.length - 1), dY);
    ctx.closePath();
    ctx.fill();

    // Drawdown line
    ctx.strokeStyle = "#f03b3b";
    ctx.lineWidth = 1;
    ctx.beginPath();
    curve.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), dY) : ctx.lineTo(px(i), pyDD(p.dd)));
    ctx.stroke();

    // Labels
    ctx.fillStyle = "#5a5f6e";
    ctx.font = "9px monospace";
    ctx.textAlign = "left";
    ctx.fillText("EQUITY", pad.l + 4, pad.t + 12);
    ctx.fillText("DRAWDOWN", pad.l + 4, dY + 12);

    ctx.textAlign = "right";
    ctx.fillText(maxDD.toFixed(1) + "%", W - pad.r - 2, dY + dH - 2);
  }, [curve]);

  return <canvas ref={canvasRef} width={600} height={240} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── P&L Heatmap ──────────────────────────────────────────────
function PnLHeatmap({ grid }) {
  // Find max abs pnl for color scaling
  let maxAbs = 1;
  grid.forEach(row => row.forEach(cell => { if (Math.abs(cell.pnl) > maxAbs) maxAbs = Math.abs(cell.pnl); }));

  const cellColor = (pnl, count) => {
    if (count === 0) return "#111318";
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    if (pnl > 0) return `rgba(0,200,150,${0.15 + intensity * 0.7})`;
    return `rgba(240,59,59,${0.15 + intensity * 0.7})`;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `40px repeat(14, 1fr)`, gap: 2, minWidth: 600 }}>
        {/* Header row */}
        <div />
        {HOURS.map(h => (
          <div key={h} style={{ fontSize: 8, color: "#3a3d4a", textAlign: "center", fontFamily: "monospace", padding: "2px 0" }}>
            {h.replace(":00", "")}
          </div>
        ))}

        {/* Data rows — only Mon-Fri */}
        {[1, 2, 3, 4, 5].map(day => (
          <React.Fragment key={day}>
            <div style={{ fontSize: 9, color: "#5a5f6e", display: "flex", alignItems: "center", fontWeight: 600 }}>{DAYS[day]}</div>
            {grid[day].map((cell, hour) => (
              <div key={hour} title={cell.count > 0 ? `${fmt.usd(cell.pnl)} (${cell.count} trades)` : "No trades"}
                style={{
                  height: 24, borderRadius: 3, background: cellColor(cell.pnl, cell.count),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 8, fontFamily: "monospace",
                  color: cell.count > 0 ? (cell.pnl >= 0 ? "#00c896" : "#f03b3b") : "transparent",
                  border: "1px solid #1a1d27", cursor: cell.count > 0 ? "default" : "default",
                }}>
                {cell.count > 0 ? (cell.pnl >= 0 ? "+" : "") + Math.round(cell.pnl) : ""}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── Main Analytics Dashboard ──────────────────────────────────
export function AnalyticsDashboard({ history = [], balance = 50000, onClose }) {
  const [tab, setTab] = useState("overview");

  const { curve, maxDD, finalBalance, peak } = buildEquityCurve(history, balance);
  const heatmap = buildHeatmap(history);
  const sessions = buildSessionStats(history);
  const dayStats = buildDayStats(history);

  const closedTrades = history.filter(t => t.pnl != null);
  const wins = closedTrades.filter(t => t.pnl > 0);
  const losses = closedTrades.filter(t => t.pnl <= 0);
  const totalPnl = closedTrades.reduce((s, t) => s + t.pnl, 0);
  const winRate = closedTrades.length ? (wins.length / closedTrades.length * 100) : 0;
  const avgWin = wins.length ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
  const avgLoss = losses.length ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;
  const profitFactor = losses.length && Math.abs(avgLoss) > 0 ?
    (wins.reduce((s, t) => s + t.pnl, 0) / Math.abs(losses.reduce((s, t) => s + t.pnl, 0))) : 0;

  // Streaks
  let maxWin = 0, maxLoss = 0, curW = 0, curL = 0;
  closedTrades.forEach(t => {
    if (t.pnl > 0) { curW++; curL = 0; maxWin = Math.max(maxWin, curW); }
    else { curL++; curW = 0; maxLoss = Math.max(maxLoss, curL); }
  });

  // Best/worst day
  const bestDay = dayStats.reduce((best, d) => d.pnl > (best?.pnl ?? -Infinity) ? d : best, null);
  const worstDay = dayStats.reduce((worst, d) => d.pnl < (worst?.pnl ?? Infinity) ? d : worst, null);

  const TABS = ["overview", "equity", "heatmap", "sessions"];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        width: 860, maxHeight: "90vh", background: "#0d0f14",
        border: "1px solid #2a2d3a", borderRadius: 10,
        display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,0,0,0.9)", overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ height: 44, display: "flex", alignItems: "center", padding: "0 16px", borderBottom: "1px solid #1e2130", flexShrink: 0, gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#e2e4e9", letterSpacing: "0.05em", marginRight: 8 }}>📊 ANALYTICS</span>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "4px 12px", fontSize: 10, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.06em", cursor: "pointer", borderRadius: 3,
              background: tab === t ? "rgba(0,200,150,0.12)" : "transparent",
              border: `1px solid ${tab === t ? "rgba(0,200,150,0.4)" : "transparent"}`,
              color: tab === t ? "#00c896" : "#5a5f6e",
            }}>{t}</button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#4a4f5e", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>

          {tab === "overview" && (
            <div>
              {/* KPI row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                {[
                  { label: "Total P&L", value: fmt.usd(totalPnl), color: totalPnl >= 0 ? "#00c896" : "#f03b3b" },
                  { label: "Win Rate", value: winRate.toFixed(1) + "%", color: winRate >= 50 ? "#00c896" : "#f03b3b" },
                  { label: "Profit Factor", value: profitFactor.toFixed(2), color: profitFactor >= 1.5 ? "#00c896" : profitFactor >= 1 ? "#f5a623" : "#f03b3b" },
                  { label: "Max Drawdown", value: maxDD.toFixed(1) + "%", color: maxDD < 5 ? "#00c896" : maxDD < 10 ? "#f5a623" : "#f03b3b" },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "12px 14px" }}>
                    <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "monospace", color }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Second row stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                {[
                  { label: "Total Trades", value: closedTrades.length },
                  { label: "Avg Win", value: fmt.usd(avgWin), color: "#00c896" },
                  { label: "Avg Loss", value: fmt.usd(avgLoss), color: "#f03b3b" },
                  { label: "Avg R:R", value: avgLoss !== 0 ? (Math.abs(avgWin / avgLoss)).toFixed(2) : "—" },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "10px 14px" }}>
                    <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace", color: color || "#e2e4e9" }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Streaks + Best/Worst */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "12px 14px" }}>
                  <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Streaks</div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: "#7a7f8e" }}>Best win streak</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#00c896" }}>{maxWin} trades</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: "#7a7f8e" }}>Worst loss streak</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#f03b3b" }}>{maxLoss} trades</span>
                  </div>
                </div>
                <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "12px 14px" }}>
                  <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Best / Worst Day</div>
                  {bestDay && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: "#5a5f6e" }}>{bestDay.date}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#00c896" }}>{fmt.usd(bestDay.pnl)}</span>
                    </div>
                  )}
                  {worstDay && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 10, color: "#5a5f6e" }}>{worstDay.date}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#f03b3b" }}>{fmt.usd(worstDay.pnl)}</span>
                    </div>
                  )}
                  {!bestDay && <div style={{ fontSize: 11, color: "#3a3d4a" }}>No trade history</div>}
                </div>
              </div>
            </div>
          )}

          {tab === "equity" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 12 }}>
                {[
                  { label: "Starting Balance", value: "$" + balance.toLocaleString(), color: "#7a7f8e" },
                  { label: "Current Balance", value: "$" + Math.round(finalBalance).toLocaleString(), color: finalBalance >= balance ? "#00c896" : "#f03b3b" },
                  { label: "Max Drawdown", value: maxDD.toFixed(2) + "%", color: maxDD < 5 ? "#00c896" : maxDD < 10 ? "#f5a623" : "#f03b3b" },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "10px 14px" }}>
                    <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace", color }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, height: 260 }}>
                {curve.length >= 2
                  ? <EquityCurveChart curve={curve} />
                  : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#3a3d4a", fontSize: 12 }}>No trade history to display</div>
                }
              </div>
            </div>
          )}

          {tab === "heatmap" && (
            <div>
              <div style={{ fontSize: 11, color: "#5a5f6e", marginBottom: 12 }}>
                P&L by day × hour (RTH 8am–10pm). Green = profitable, Red = losing. Intensity = magnitude.
              </div>
              <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: 12 }}>
                <PnLHeatmap grid={heatmap} />
              </div>
            </div>
          )}

          {tab === "sessions" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 16 }}>
                {Object.entries(sessions).map(([name, s]) => (
                  <div key={name} style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, padding: "12px 14px" }}>
                    <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{name}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "monospace", color: s.pnl >= 0 ? "#00c896" : "#f03b3b", marginBottom: 6 }}>
                      {s.pnl >= 0 ? "+" : ""} ${s.pnl.toFixed(0)}
                    </div>
                    <div style={{ fontSize: 10, color: "#5a5f6e" }}>{s.count} trades · {s.count > 0 ? ((s.wins / s.count) * 100).toFixed(0) : 0}% win</div>
                  </div>
                ))}
              </div>

              {/* Day-by-day table */}
              <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 6, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1e2130" }}>
                      {["Date", "Trades", "P&L", ""].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "#4a4f5e", letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dayStats.slice().reverse().map((d, i) => (
                      <tr key={d.date} style={{ borderBottom: "1px solid #111318", background: i % 2 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "7px 12px", fontSize: 11, color: "#7a7f8e" }}>{d.date}</td>
                        <td style={{ padding: "7px 12px", fontSize: 11, fontFamily: "monospace", color: "#5a5f6e" }}>{d.count}</td>
                        <td style={{ padding: "7px 12px", fontSize: 12, fontFamily: "monospace", fontWeight: 600, color: d.pnl >= 0 ? "#00c896" : "#f03b3b" }}>
                          {d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(0)}
                        </td>
                        <td style={{ padding: "7px 12px" }}>
                          <div style={{ width: Math.min(100, Math.abs(d.pnl) / 10), height: 4, background: d.pnl >= 0 ? "#00c896" : "#f03b3b", borderRadius: 2, opacity: 0.6 }} />
                        </td>
                      </tr>
                    ))}
                    {dayStats.length === 0 && (
                      <tr><td colSpan={4} style={{ padding: "24px", textAlign: "center", color: "#2a2d3a", fontSize: 12 }}>No trade history</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
